import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../shared/button/button";
import { NormalText, TooltipStyled } from "../../shared/styles";
import { Box, useMediaQuery } from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTransactionsByUserIdOrTransactionId } from "../../redux/actions/transactions.actions";
import { Loader } from "../../shared/loader/loader";
import { ContainerDetailStyled, ContainerInfoLineStyled } from "./styles";
import { DividerStyled } from "../../shared/divider/divider";
import { Copy } from "@phosphor-icons/react";
import {
  copyToClipboard,
  formatDate,
  formatLargeText,
  getTokenIcon,
} from "../../shared/utils";
import html2pdf from "html2pdf.js";

export const TransactionDetailContainer = ({ transactionId }) => {
  const {
    data: transaction,
    isLoading: isLoadingTransactions,
    isError: isErrorTransactions,
  } = useSelector((state) => state.transactions);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isMobile = useMediaQuery("(max-width: 800px)");

  const { transactionId: transactionIdUrl } = useParams();

  const handleDownloadPDF = () => {
    const element = document.getElementById("transaction-detail");

    // Mostrar elementos solo para PDF
    const pdfOnlyElements = element.querySelectorAll(".pdf-only");
    pdfOnlyElements.forEach((el) => (el.style.display = "block"));

    // Ocultar elementos que no deben aparecer en PDF
    const screenOnlyElements = element.querySelectorAll(".screen-only");
    screenOnlyElements.forEach((el) => (el.style.display = "none"));

    const opt = {
      margin: 1,
      filename: `transaction-${transaction?.signedtx || "unknown"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => {
        // Restaurar visibilidad original después de generar el PDF
        pdfOnlyElements.forEach((el) => (el.style.display = "none"));
        screenOnlyElements.forEach((el) => (el.style.display = ""));
      })
      .catch(() => {
        // En caso de error, también restaurar
        pdfOnlyElements.forEach((el) => (el.style.display = "none"));
        screenOnlyElements.forEach((el) => (el.style.display = ""));
      });
  };

  useEffect(() => {
    if (transactionId || transactionIdUrl) {
      dispatch(
        getTransactionsByUserIdOrTransactionId({
          transactionId: transactionId || transactionIdUrl,
        })
      );
    }
  }, [dispatch, transactionId, transactionIdUrl]);

  if (isErrorTransactions) {
    return (
      <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
        <NormalText color="#0f110c" size={14} isBold>
          Error loading transaction details
        </NormalText>
      </Box>
    );
  }

  return isLoadingTransactions ? (
    <Loader />
  ) : (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gap={"16px"}
      alignItems={"flex-end"}
      maxWidth={"800px"}
      width={"100%"}
    >
      <div id="transaction-detail" style={{ width: "100%" }}>
        <ContainerDetailStyled>
          <Button
            startIcon={"CaretLeft"}
            onClick={() => navigate("/transactions")}
            type={"regular"}
            iconSize={14}
            containerStyles={{
              position: "absolute",
              top: "16px",
              left: "16px",
            }}
            className="screen-only"
          />
          <Box
            display={"flex"}
            alignItems={"center"}
            flexDirection={"column"}
            gap={"8px"}
          >
            <img src="/logo-tenui.png" alt="Logo Tenui" width={96} />
            <NormalText color="#0f110c" size={16} isBold>
              Transaction receipt
            </NormalText>
            <NormalText color="#0f110c" size={12}>
              Date: {formatDate(transaction?.created_at)}
            </NormalText>
          </Box>
          <DividerStyled />
          <NormalText
            color="#0f110c"
            size={14}
            isBold
            sx={{ marginBottom: "24px" }}
          >
            Operation details
          </NormalText>
          <Box display={"flex"} flexDirection={"column"} gap={"16px"}>
            <ContainerInfoLineStyled>
              <NormalText color="#8C8C8C" size={12}>
                ID
              </NormalText>
              <Box display={"flex"} gap={"8px"} alignItems={"center"}>
                <NormalText color="#0f110c" size={12}>
                  {isMobile
                    ? formatLargeText(transaction?.signedtx)
                    : transaction?.signedtx}
                </NormalText>
                <TooltipStyled
                  title="Copy"
                  placement="top"
                  withCopy
                  className="screen-only"
                >
                  <Copy
                    size={16}
                    color="#8C8C8C"
                    onClick={() => copyToClipboard(transaction?.signedtx)}
                  />
                </TooltipStyled>
              </Box>
            </ContainerInfoLineStyled>
            <ContainerInfoLineStyled>
              <NormalText color="#8C8C8C" size={12}>
                From
              </NormalText>
              <Box display={"flex"} gap={"8px"} alignItems={"center"}>
                <NormalText color="#0f110c" size={12}>
                  {isMobile
                    ? formatLargeText(transaction?.from, 40)
                    : transaction?.from}
                </NormalText>
                <TooltipStyled
                  title="Copy"
                  placement="top"
                  withCopy
                  className="screen-only"
                >
                  <Copy
                    size={16}
                    color="#8C8C8C"
                    onClick={() => copyToClipboard(transaction?.from)}
                  />
                </TooltipStyled>
              </Box>
            </ContainerInfoLineStyled>
            <ContainerInfoLineStyled>
              <NormalText color="#8C8C8C" size={12}>
                To
              </NormalText>
              <Box display={"flex"} gap={"8px"} alignItems={"center"}>
                <NormalText color="#0f110c" size={12}>
                  {isMobile
                    ? formatLargeText(transaction?.to, 40)
                    : transaction?.to}
                </NormalText>
                <TooltipStyled
                  title="Copy"
                  placement="top"
                  withCopy
                  className="screen-only"
                >
                  <Copy
                    size={16}
                    color="#8C8C8C"
                    onClick={() => copyToClipboard(transaction?.to)}
                  />
                </TooltipStyled>
              </Box>
            </ContainerInfoLineStyled>
            <ContainerInfoLineStyled>
              <NormalText color="#8C8C8C" size={12}>
                Amount
              </NormalText>
              <Box display={"flex"} gap={"8px"} alignItems={"center"}>
                {getTokenIcon(transaction?.token) && (
                  <img
                    src={getTokenIcon(transaction?.token)}
                    alt={transaction?.token}
                    style={{ width: 16, height: 16 }}
                  />
                )}
                <NormalText color="#0f110c" size={12}>
                  {transaction?.amount} {transaction?.token}
                </NormalText>
              </Box>
            </ContainerInfoLineStyled>
            <ContainerInfoLineStyled>
              <NormalText color="#8C8C8C" size={12}>
                Fee
              </NormalText>
              <Box display={"flex"} gap={"8px"} alignItems={"center"}>
                {getTokenIcon(transaction?.token) && (
                  <img
                    src={getTokenIcon(transaction?.token)}
                    alt={transaction?.token}
                    style={{ width: 16, height: 16 }}
                  />
                )}
                <NormalText color="#0f110c" size={12}>
                  {transaction?.comision_amount} {transaction?.token}
                </NormalText>
              </Box>
            </ContainerInfoLineStyled>
            <ContainerInfoLineStyled>
              <NormalText color="#8C8C8C" size={12}>
                Net amount sent
              </NormalText>
              <Box display={"flex"} gap={"8px"} alignItems={"center"}>
                {getTokenIcon(transaction?.token) && (
                  <img
                    src={getTokenIcon(transaction?.token)}
                    alt={transaction?.token}
                    style={{ width: 16, height: 16 }}
                  />
                )}
                <NormalText color="#0f110c" size={12}>
                  {transaction?.amount - transaction?.comision_amount}{" "}
                  {transaction?.token}
                </NormalText>
              </Box>
            </ContainerInfoLineStyled>
          </Box>
          <Box className="pdf-only" display={"none"}>
            <DividerStyled />
            <Box display={"flex"} justifyContent={"center"} gap={"8px"}>
              <NormalText color="#0f110c" size={12}>
                Thank you for using our portal.
              </NormalText>
            </Box>
          </Box>
        </ContainerDetailStyled>
      </div>
      <Button label={"Download"} onClick={handleDownloadPDF} />
    </Box>
  );
};
